import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AdminDeposit,
    useCaptureDepositMutation,
} from '../../../api/deposit/depositAdminApiSlice';
import CurrencyAmountInput from '../../../components/CurrencyAmountInput';
import { getCurrentLang } from '../../../i18n/i18n';
import { formatCurrency } from '../../../utils';
import { isValidCaptureAmount } from '../../../utils/depositValidation';
import DepositModalShell, { ReasonField } from './DepositModalShell';
import { useDepositAction } from './useDepositAction';

interface Props {
    deposit: AdminDeposit;
    onClose: () => void;
}

// Capture part or all of the deposit hold — a real-money action, so both the
// amount and a reason are required (the reason is stored as
// deposit_capture_reason and audited server-side).
const CaptureDepositModal = ({ deposit, onClose }: Props) => {
    const { t } = useTranslation();
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    // Firefox reports non-numeric input on <input type="number"> as
    // value === '' with validity.badInput set — without this guard that
    // would silently read as "empty".
    const [hasBadInput, setHasBadInput] = useState(false);
    const [captureDeposit, { isLoading }] = useCaptureDepositMutation();
    const { formError, clearFormError, submit } = useDepositAction('CaptureDepositModal', onClose);

    // Validation derives from form state (not API data), per the edit-form
    // rule. The amount rules (0 < amount <= deposit, max two decimals) live
    // in the pure, unit-tested isValidCaptureAmount.
    const trimmedAmount = amount.trim();
    const isAmountValid =
        !hasBadInput && isValidCaptureAmount(trimmedAmount, deposit.depositAmount);
    const isReasonValid = reason.trim().length > 0;
    const isValid = isAmountValid && isReasonValid;

    // Inline validation feedback: the save button is disabled while invalid,
    // so the message must show as the user types (badInput leaves the value
    // empty, hence the extra check).
    const showValidation = !isAmountValid && (trimmedAmount.length > 0 || hasBadInput);

    const handleCapture = () => {
        if (!isValid) return;
        submit(() =>
            captureDeposit({
                orderId: deposit.orderId,
                amount: Number(trimmedAmount),
                reason: reason.trim(),
            }).unwrap(),
        );
    };

    return (
        <DepositModalShell
            title={t('deposits.capture.title')}
            description={t('deposits.capture.description', {
                orderNumber: deposit.orderNumber,
                amount: formatCurrency(deposit.depositAmount, getCurrentLang()),
            })}
            confirmLabel={t('deposits.capture.confirm')}
            confirmDisabled={!isValid}
            loading={isLoading}
            onConfirm={handleCapture}
            onClose={onClose}
        >
            {/* B9 exclusion (spec §2): lateness is billed by the overtime
                mechanism, never captured from the deposit a second time. */}
            {deposit.overtimeFee > 0 && (
                <p className={'text-sm text-foreground/60'}>
                    {t('deposits.capture.overtime_hint', {
                        fee: formatCurrency(deposit.overtimeFee, getCurrentLang()),
                    })}
                </p>
            )}
            <CurrencyAmountInput
                id={'capture-amount'}
                label={t('deposits.capture.amount_label')}
                value={amount}
                autoFocus
                onValueChange={(value, badInput) => {
                    setAmount(value);
                    setHasBadInput(badInput);
                    clearFormError();
                }}
                validationMessage={
                    showValidation
                        ? t('deposits.capture.validation', {
                            max: formatCurrency(deposit.depositAmount, getCurrentLang()),
                        })
                        : null
                }
            />
            <ReasonField
                id={'capture-reason'}
                label={t('deposits.capture.reason_label')}
                value={reason}
                onChange={(value) => {
                    setReason(value);
                    clearFormError();
                }}
            />
            {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
        </DepositModalShell>
    );
};

export default CaptureDepositModal;
